import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { useGradeMappings } from "@redotech/redo-merchant-managed-returns-app/use-grade-mappings";
import type { ProcessingActionState } from "@redotech/redo-return-core-common/return-flow-step-valid";
import { ProcessingActionType } from "@redotech/redo-return-core-common/return-flow/processing-action";
import {
  DEFAULT_WAREHOUSE_GRADE_NAMES,
  GradeAcceptance,
  type GradeMapping,
  resolveGradeActions,
  resolveWarehouseGradeNames,
} from "@redotech/redo-return-settings-common/grade-mapping";
import { StepTypeDetailsProps } from "@redotech/redo-return-ui/flows/step";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";

export const ProcessingActionDetails = memo(function ProcessingActionDetails({
  stepSelect,
  state,
  setState,
}: StepTypeDetailsProps<ProcessingActionState>) {
  const { t } = useTranslation();
  const team = useRequiredContext(TeamContext);

  const gradeChoices = useMemo<readonly string[]>(() => {
    const names = resolveWarehouseGradeNames({
      customGradesEnabled:
        team.settings?.returnItemDisposition?.enabled ?? false,
      gradeOptions: team.settings?.returnItemDisposition?.gradeOptions,
    });
    return names.length > 0 ? names : DEFAULT_WAREHOUSE_GRADE_NAMES;
  }, [team.settings?.returnItemDisposition]);

  const { gradeMappings } = useGradeMappings();
  const dropdownOptions = useMemo(
    () =>
      resolveGradeActions(gradeChoices, gradeMappings ?? []).map(
        ({ gradeName, mapping }) => ({
          type: "text" as const,
          id: gradeName,
          text: mapping
            ? `${gradeName} → ${describeOutcome(mapping, t)}`
            : gradeName,
          value: gradeName,
        }),
      ),
    [gradeChoices, gradeMappings, t],
  );

  return (
    <Flex dir="column" gap="md">
      {state.action.type === ProcessingActionType.AssignGrade && (
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {t("Grade")}
          </Text>
          <RedoSingleSelectDropdownInput<string>
            options={dropdownOptions}
            optionSelected={({ item }) =>
              setState((state) => ({
                ...state,
                action:
                  state.action.type === ProcessingActionType.AssignGrade
                    ? { ...state.action, grade: item.value }
                    : state.action,
              }))
            }
            selectedItem={dropdownOptions.find(
              (item) => item.value === state.action.grade,
            )}
            size="xs"
          />
        </Flex>
      )}
      <Flex dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          {t("Next step")}
        </Text>
        {stepSelect({
          value: state.next,
          valueChange: (next) => setState((state) => ({ ...state, next })),
          handleIndex: 0,
        })}
      </Flex>
    </Flex>
  );
});

function describeOutcome(
  mapping: GradeMapping,
  t: (key: string) => string,
): string {
  if (mapping.acceptance === GradeAcceptance.REJECT) {
    return t("Reject");
  }
  if (mapping.acceptance === GradeAcceptance.FLAG_FOR_REVIEW) {
    return t("Flag for review");
  }
  return mapping.restock ? t("Restock") : t("Accept");
}
